// Mapping between API enum values and UI labels for the Platform Banner feature.
// Wherever possible, label/colour maps from the broader app are reused so the
// Dashboard Banner page stays visually consistent with the rest of the admin
// panel (Manage Features, Plan Requests, Creator Payables, etc.).

import {
  TIER_COLOR_MAP,
  TIER_NAME_MAP,
  WL_PLAN_COLOR_MAP,
} from "../../Utils/constants";

export const PLATFORMS = {
  TAGMANGO: "tagmango",
  WHITELABEL: "whitelabel",
};

export const PLATFORM_SETUPS = {
  TAGMANGO: "tagmango",
  FREEDOM: "freedom",
  ENTERPRISE: "enterprise",
};

export const TIERS = {
  TIER1: "tier1",
  TIER2: "tier2",
  TIER3: "tier3",
  TIER4: "tier4",
};

// Allowed (platform → setup → tiers[]) matrix per backend doc.
export const COMBO_MATRIX = {
  [PLATFORMS.TAGMANGO]: {
    [PLATFORM_SETUPS.TAGMANGO]: [TIERS.TIER1],
    [PLATFORM_SETUPS.FREEDOM]: [TIERS.TIER1],
    [PLATFORM_SETUPS.ENTERPRISE]: [TIERS.TIER1],
  },
  [PLATFORMS.WHITELABEL]: {
    [PLATFORM_SETUPS.FREEDOM]: [TIERS.TIER1, TIERS.TIER2, TIERS.TIER3],
    [PLATFORM_SETUPS.ENTERPRISE]: [
      TIERS.TIER1,
      TIERS.TIER2,
      TIERS.TIER3,
      TIERS.TIER4,
    ],
  },
};

export const PLATFORM_LABEL = {
  [PLATFORMS.TAGMANGO]: "TagMango",
  [PLATFORMS.WHITELABEL]: "White Label",
};

// Reuse the app-wide tier label map (tier1 -> Basic ... tier4 -> Ultimate).
export const TIER_LABEL = TIER_NAME_MAP;

// Setup label depends on the platform: `tagmango+tagmango` is shown as "Free"
// per design. The other two values map to the same words used in
// `WL_PLAN_TITLE` ("Freedom Plan" / "Enterprise Plan") minus the "Plan" suffix
// because the design uses single-word tags.
export const getSetupLabel = (platform, setup) => {
  if (platform === PLATFORMS.TAGMANGO && setup === PLATFORM_SETUPS.TAGMANGO) {
    return "Free";
  }
  if (setup === PLATFORM_SETUPS.FREEDOM) return "Freedom";
  if (setup === PLATFORM_SETUPS.ENTERPRISE) return "Enterprise";
  return setup;
};

export const getPlatformOptions = () =>
  Object.keys(COMBO_MATRIX).map((platform) => ({
    value: platform,
    label: PLATFORM_LABEL[platform],
  }));

export const getSetupOptions = (platform) => {
  if (!platform || !COMBO_MATRIX[platform]) return [];
  return Object.keys(COMBO_MATRIX[platform]).map((setup) => ({
    value: setup,
    label: getSetupLabel(platform, setup),
  }));
};

export const getTierOptions = (platform, setup) => {
  if (!platform || !setup || !COMBO_MATRIX[platform]?.[setup]) return [];
  return COMBO_MATRIX[platform][setup].map((tier) => ({
    value: tier,
    label: TIER_LABEL[tier] || tier,
  }));
};

// Listing-page filters: setup options depend on the platform tab. Pass
// `platform === undefined` for the "All" tab.
export const getAvailableSetupOptionsForFilter = (platform) => {
  const platforms = platform ? [platform] : Object.keys(COMBO_MATRIX);
  const seen = new Set();
  const opts = [];
  platforms.forEach((p) => {
    Object.keys(COMBO_MATRIX[p] || {}).forEach((s) => {
      if (seen.has(s)) return;
      seen.add(s);
      opts.push({ value: s, label: getSetupLabel(p, s) });
    });
  });
  return opts;
};

// Listing-page filters: tier options depend on the platform tab AND the
// currently chosen setup filter (when set).
export const getAvailableTierOptionsForFilter = (platform, setup) => {
  const platforms = platform ? [platform] : Object.keys(COMBO_MATRIX);
  const tierSet = new Set();
  platforms.forEach((p) => {
    const matrix = COMBO_MATRIX[p];
    if (!matrix) return;
    if (setup) {
      (matrix[setup] || []).forEach((t) => tierSet.add(t));
    } else {
      Object.values(matrix).forEach((tiers) =>
        tiers.forEach((t) => tierSet.add(t))
      );
    }
  });
  return Object.values(TIERS)
    .filter((t) => tierSet.has(t))
    .map((t) => ({ value: t, label: TIER_LABEL[t] || t }));
};

// Flattened list of every allowed combination across the matrix.
export const getAllAllowedCombos = () => {
  const combos = [];
  Object.keys(COMBO_MATRIX).forEach((platform) => {
    Object.keys(COMBO_MATRIX[platform]).forEach((setup) => {
      COMBO_MATRIX[platform][setup].forEach((tier) => {
        combos.push({ platform, platformSetup: setup, tier });
      });
    });
  });
  return combos;
};

export const comboKey = ({ platform, platformSetup, tier }) =>
  `${platform}|${platformSetup}|${tier}`;

// Canonical sort order: traverses COMBO_MATRIX top-to-bottom, so banners line
// up as TagMango → Free/Freedom/Enterprise → tier1..tierN, then White Label
// → Freedom/Enterprise → tier1..tierN. Anything outside the matrix is pushed
// to the end (preserves relative order via fallback).
const COMBO_SORT_INDEX = (() => {
  const map = {};
  getAllAllowedCombos().forEach((combo, idx) => {
    map[comboKey(combo)] = idx;
  });
  return map;
})();

export const compareBannersByCombo = (a, b) => {
  const aIdx = COMBO_SORT_INDEX[comboKey(a)];
  const bIdx = COMBO_SORT_INDEX[comboKey(b)];
  const aRank = aIdx ?? Number.MAX_SAFE_INTEGER;
  const bRank = bIdx ?? Number.MAX_SAFE_INTEGER;
  return aRank - bRank;
};

// Setup tag colour: reuses the same palette as the existing white-label plan
// tags (`WL_PLAN_COLOR_MAP`) so the design language matches the rest of the
// app. The "Free" setup (only valid on tagmango) gets antd's default (light
// gray) so it reads as a neutral / non-paid plan.
export const SETUP_TAG_COLOR = {
  [PLATFORM_SETUPS.TAGMANGO]: undefined, // "Free" → default light gray
  [PLATFORM_SETUPS.FREEDOM]: WL_PLAN_COLOR_MAP["freedom-plan"], // "cyan"
  [PLATFORM_SETUPS.ENTERPRISE]: WL_PLAN_COLOR_MAP["enterprise-plan"], // "magenta"
};

// Reuse the app-wide tier colour map (tier1: orange, tier2: blue, etc.).
export const TIER_TAG_COLOR = TIER_COLOR_MAP;

// Per the design's "Upload Banner Image" hint.
// CS team uploads relatively heavy creatives, so we allow up to 1 MB.
export const BANNER_IMAGE_HINT = {
  minWidth: 1200,
  minHeight: 185,
  maxSizeKB: 1024,
  maxSizeLabel: "1 MB",
};
